// components/PresenceIndicator.tsx
import React from 'react';
import { Users } from 'lucide-react';

// Simplified interface without avatar_url
interface PresenceUser {
  userId: string;
  userName: string;
  userColor: string;
  online_at: string;
}

interface PresenceIndicatorProps {
  users: PresenceUser[];
  currentUserId: string;
  className?: string;
}

export function PresenceIndicator({ users, currentUserId, className = '' }: PresenceIndicatorProps) {
  // Get all unique users (including current user)
  const uniqueUsers = users.reduce((acc, user) => {
    if (!acc.find(u => u.userId === user.userId)) {
      acc.push(user);
    }
    return acc;
  }, [] as PresenceUser[]);

  // Count other collaborators (excluding current user)
  const otherCollaborators = uniqueUsers.filter(user => user.userId !== currentUserId).length;

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="flex items-center gap-2">
        <Users className="w-4 h-4 text-green-500" />
        <span className="text-sm text-gray-600">
          {otherCollaborators === 0 
            ? 'Working alone' 
            : `${otherCollaborators} other collaborator${otherCollaborators !== 1 ? 's' : ''} online`
          }
        </span>
      </div>
      
      {/* Optional: Show colored dots for other users */}
      {otherCollaborators > 0 && (
        <div className="flex -space-x-1">
          {uniqueUsers
            .filter(user => user.userId !== currentUserId)
            .slice(0, 5)
            .map((user, index) => (
              <div
                key={user.userId}
                className="w-3 h-3 rounded-full border border-white shadow-sm"
                style={{ backgroundColor: user.userColor }}
                title={user.userName}
              />
            ))}
          {otherCollaborators > 5 && (
            <div className="w-3 h-3 rounded-full bg-gray-500 border border-white shadow-sm flex items-center justify-center">
              <span className="text-[8px] text-white font-bold">+{otherCollaborators - 5}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}